import { Uuid } from "@/domains/global/identifiers";
import { isExternalHttpsImageUrl } from "@/store/note-attachments/isExternalHttpsImageUrl";
import {
  resolveNoteAttachmentUuid,
  resolveSpaceAccountNoteAttachmentSyncModelUuid,
} from "@/store/note-attachments/resolveAttachmentUuid";
import { BatchCreateSpaceAccountNoteAttachmentItem } from "@/store/sync/operations/types";

// Matches the attachment lifecycle worker's external-image MIME hint: the BE
// represents external image rows as webp. `mime_type` isn't part of the id, so
// this only keeps the optimistic row consistent until the BE supersedes it on save.
const EXTERNAL_IMAGE_MIME_TYPE_HINT = "image/webp";

const filenameFromUrl = (src: string): string => {
  try {
    const segments = new URL(src).pathname.split("/");
    return segments[segments.length - 1] ?? "";
  } catch {
    return "";
  }
};

/**
 * Builds the optimistic `hosting_status=PENDING` create items for freshly
 * pasted/dropped external images. Filters to the BE's external-internalization
 * set (`isExternalHttpsImageUrl`) and dedupes, so each item's id matches the
 * row the BE derives on save and the two converge instead of duplicating.
 *
 * Srcs whose row already exists are skipped (create-if-absent): re-creating
 * would clobber an already-READY row back to PENDING (the optimistic UPSERT
 * replaces the whole row). `existingModelIds` are SpaceAccountNoteAttachment
 * sync-model ids (what `NoteAttachmentsObservable.attachmentIds` holds), so we
 * compare each candidate's model id, not its business attachment id.
 */
export const buildExternalImageCreateItems = ({
  noteId,
  srcs,
  existingModelIds,
}: {
  noteId: Uuid;
  srcs: string[];
  existingModelIds?: ReadonlySet<string>;
}): BatchCreateSpaceAccountNoteAttachmentItem[] => {
  const seen = new Set<string>();
  const items: BatchCreateSpaceAccountNoteAttachmentItem[] = [];

  for (const src of srcs) {
    if (seen.has(src) || !isExternalHttpsImageUrl(src)) continue;
    seen.add(src);

    const attachmentId = resolveNoteAttachmentUuid(noteId, src);
    const modelId = resolveSpaceAccountNoteAttachmentSyncModelUuid(attachmentId);
    if (existingModelIds?.has(modelId)) continue;

    items.push({
      associated_note_attachment_id: attachmentId,
      associated_note_id: noteId,
      attachment_url: src,
      mime_type: EXTERNAL_IMAGE_MIME_TYPE_HINT,
      filename: filenameFromUrl(src),
      page_count: null,
      hosting_status: "PENDING",
    });
  }

  return items;
};
